import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';

import { InitialValues, ValidationErrors } from 'forms/types';
import {
  composeValidators,
  getTodayDateTime,
  isGreater,
  isGreaterOrEqual,
  isPresent,
} from 'forms/validators';

import { OccupationFormValues } from '../form/types';

export const validate = ({
  job,
  atLeast30HoursAWeek,
  yearsOfExperience,
  governmentEmployee,
  workFromHomePercentage,
  selfEmployed,
  corporateStructure,
  ownershipStake,
  numberOfEmployees,
  numberOfPartners,
  companyFoundedAt,
  annualIncomeBeforeCompany,
  becameOwnerAt,
  educationLevel,
  individualIncome,
  disabilityInsuranceStatus,
  existingCoverage,
  disabilityInsuranceSource,
  intendToReplace,
  employerPaysPremium,
}: InitialValues<OccupationFormValues>): ValidationErrors<OccupationFormValues> => {
  const errors: ValidationErrors<OccupationFormValues> = {
    job: isPresent(job),
    atLeast30HoursAWeek: isPresent(atLeast30HoursAWeek),
    yearsOfExperience: composeValidators(isPresent, isGreater(0))(yearsOfExperience),
    governmentEmployee: isPresent(governmentEmployee),
    workFromHomePercentage: isPresent(workFromHomePercentage),
    selfEmployed: isPresent(selfEmployed),
    educationLevel: isPresent(educationLevel),
    individualIncome: isPresent(individualIncome),
    disabilityInsuranceStatus: isPresent(disabilityInsuranceStatus),
  };

  if (individualIncome === 0) {
    errors.individualIncome = 'You must have an income to qualify for disability coverage';
  }

  if (selfEmployed) {
    const atLeast0 = composeValidators(isPresent, isGreaterOrEqual(0));

    errors.corporateStructure = isPresent(corporateStructure);
    errors.ownershipStake = isPresent(ownershipStake);
    errors.numberOfEmployees = atLeast0(numberOfEmployees);
    errors.numberOfPartners = atLeast0(numberOfPartners);
    errors.companyFoundedAt = isPresent(companyFoundedAt);
    errors.becameOwnerAt = isPresent(becameOwnerAt);

    if (companyFoundedAt && companyFoundedAt >= getTodayDateTime().year - 1) {
      errors.annualIncomeBeforeCompany = isPresent(annualIncomeBeforeCompany);
    }
  }

  if (disabilityInsuranceStatus) {
    errors.disabilityInsuranceSource = isPresent(disabilityInsuranceSource);
    errors.intendToReplace = isPresent(intendToReplace);
    errors.existingCoverage = isPresent(existingCoverage);
    if (individualIncome != null) {
      const cap = Math.floor((0.7 * (individualIncome ?? 0)) / 12);

      if (existingCoverage && existingCoverage > cap) {
        errors.existingCoverage = `Coverage is usually capped at 60-70% of individual income (in your case: $${cap}). If you are looking for replacement coverage, please set this field to $0. Our experts will be available to discuss your particular circumstances once you have received your quotes.`;
      }
    }

    if (disabilityInsuranceSource === 'employer') {
      errors.employerPaysPremium = isPresent(employerPaysPremium);
    }
  }

  return omitBy(errors, isNil);
};
